<script lang="ts">
    import cn from 'classnames';
    import { onMount } from 'svelte';
    import { classList } from 'svelte-body';
    import { debounce } from 'throttle-debounce';

    import { bodyClass } from '@components/stores';

    import type { ScrollBehavior, ScrollDirection } from './types';

    import s from './slider.module.css';

    // eslint-disable-next-line no-undef
    type T = $$Generic<object & { id: string }>;

    export let classes = '';
    export let data: T[] = [];
    export let currentIndex = 0;
    export let onSlideChange: (index: number, prevIndex: number, direction: ScrollDirection) => void;

    export const gotoSlide = (index: number, options?: { behavior?: ScrollBehavior; direction?: ScrollDirection }) => {
        const validIndex = validateIndex(index);
        emitChange(validIndex, options?.direction ?? 'forward');
        slideEls[validIndex]?.scrollIntoView({ behavior: options?.behavior ?? 'smooth' });
    };
    export const nextSlide = () => gotoSlide(getScrolledIndex() + 1, { behavior: 'smooth', direction: 'forward' });
    export const prevSlide = () => gotoSlide(getScrolledIndex() - 1, { behavior: 'smooth', direction: 'backward' });

    let rootEl: HTMLElement;
    let wrapperEl: HTMLElement;
    let slideEls: HTMLElement[] = [];

    function validateIndex(index: number): number {
        if (index > data.length - 1) {
            return 0;
        }

        if (index < 0) {
            return 0;
        }

        return index;
    }

    const onScroll = debounce(10, () => {
        const index = getScrolledIndex();
        if (index === currentIndex) {
            return;
        }

        if (!slideEls[index]) {
            gotoSlide(0, { direction: 'forward' });
            return;
        }

        emitChange(index, index > currentIndex ? 'forward' : 'backward');
    });

    function emitChange(index: number, direction: ScrollDirection) {
        onSlideChange && onSlideChange(index, currentIndex, direction);
        currentIndex = index;
    }

    const onKeydown = (e: KeyboardEvent) => {
        switch (e.code) {
            case 'ArrowUp':
                prevSlide();
                break;
            case 'ArrowDown':
                nextSlide();
                break;
            default:
                break;
        }
    };

    function getScrolledIndex() {
        return Math.max(Math.round(wrapperEl.scrollTop / wrapperEl.offsetHeight), 0);
    }

    let rootHeight = 0;
    function setRootHeight() {
        if (rootHeight !== rootEl.offsetHeight) {
            rootHeight = rootEl.offsetHeight;
            rootEl.style.setProperty('--ff-slider-height', `${rootHeight}px`);
        }
    }

    onMount(() => {
        setRootHeight();
        gotoSlide(currentIndex, { behavior: 'instant', direction: 'forward' });
    });
</script>

<svelte:window on:keydown={onKeydown} on:resize={setRootHeight} />
<svelte:body use:classList={cn(s.withSlider, $bodyClass)} />

<div class={cn(s.root, classes)} bind:this={rootEl}>
    <div class={s.wrapper} bind:this={wrapperEl} on:scroll={onScroll}>
        {#each data as item, index (`${item.id}-${index}`)}
            <div class={s.slide} bind:this={slideEls[index]}>
                <slot dataItem={{ ...item, index }}></slot>
            </div>
        {/each}
        <div class={cn(s.slide, 'fake-slide')}></div>
    </div>
</div>
